import { calculateFees } from '@/apis/payments/requests'
import { Button } from '@/app/common/components/Button'
import { Card, CardContent, CardHeader } from '@/app/common/components/Card'
import { CurrencyInputField } from '@/app/common/components/currency-input/currency-input'
import FormErrorMessage from '@/app/common/components/FormErrorMessage'
import MultiQueryPageLoader from '@/app/common/components/PageLoader/MultiQueryPageLoader'
import { getTotalAmountToBePaidByApplicant } from '@/app/common/payment.utils'
import { Provider } from '@/schemas/provider.schema'
import { Label } from '@/shad/ui/label'
import { selectFamilyApplication } from '@/storage/applications/familyApplicationSlice'
import { selectProgramID } from '@/storage/program_settings/programSettingsSlice'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import { useDebounce } from '@uidotdev/usehooks'
import { t } from 'i18next'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import FamilyWizardLayout from '../../family_application/FamilyWizardLayout'
import { SubmitButton } from '../../family_application/student_details/shared/submit-button'
import { useStudentPurses } from '../api/usePaymentProviders'
import {
  AddDetailsToOrderForm,
  AddDetailsToOrderFormSchema,
} from '../schema/providers.schema'
import PaymentFeesResultsCard from './PaymentFeesResultsCard'

type Props = {
  defaultTotal: number
  provider: Provider
  onNext: (details: AddDetailsToOrderForm) => void
  onBack: () => void
}

export const AddDetails = (props: Props) => {
  const { defaultTotal, onNext, provider, onBack } = props

  const { t: translate } = useTranslation()
  const application = useSelector(selectFamilyApplication)
  const programId = useSelector(selectProgramID)
  const {
    watch,
    handleSubmit,
    getValues,
    control,
    formState: { errors },
  } = useForm<AddDetailsToOrderForm>({
    resolver: zodResolver(AddDetailsToOrderFormSchema),
    defaultValues: {
      payment_amount: defaultTotal,
    },
  })

  const debounceSearch = useDebounce(getValues().payment_amount, 500)
  const studentPursesQuery = useStudentPurses({
    application_id: application.application_id,
    program_id: programId,
  })

  const onSubmit: SubmitHandler<AddDetailsToOrderForm> = async (
    data: AddDetailsToOrderForm
  ) => {
    onNext(data)
  }

  const isValidCalculation = !isNaN(debounceSearch) && debounceSearch > 0
  const calculateFeesQuery = useQuery({
    queryFn: async () =>
      await calculateFees({
        base_amount: debounceSearch,
        program_id: programId,
      }),
    queryKey: ['calculateFees', debounceSearch, programId],
    enabled: isValidCalculation,
  })

  const onChange = () => {
    onBack()
  }


  const currencyFormatter = new Intl.NumberFormat('en-US', {
    currency: 'USD',
    style: 'currency',
  })

  watch('payment_amount')

  return (
    <FamilyWizardLayout>
      <FamilyWizardLayout.Header>
        <h1>{translate('add_details_to_your_order')}</h1>
      </FamilyWizardLayout.Header>
      <FamilyWizardLayout.Content className="w-full">
        <MultiQueryPageLoader queries={[studentPursesQuery] as const}>
          {([studentPurses]) => {
            const maxAmount = studentPurses.reduce((acc, curr) => {
              return acc + (curr.balance_available || 0)
            }, 0)

            function isAboveMaximumAmount() {
              if (!calculateFeesQuery?.data) {
                return false
              }

              const totalAmount = getTotalAmountToBePaidByApplicant(
                calculateFeesQuery.data
              )

              return totalAmount > maxAmount
            }

            const isAboveMaxAmount = isAboveMaximumAmount()

            return (
              <div>
                <Card className="w-full">
                  <CardHeader>
                    <h2 className="flex text-base w-full  text-left font-medium text-gray-800 ">
                      {translate('order_details')}
                    </h2>
                    <p className="flex w-full text-sm font-normal text-gray-600">
                      {provider.name}
                    </p>
                  </CardHeader>
                  <CardContent className="w-full">
                    <form
                      id="add-details-form"
                      onSubmit={handleSubmit(onSubmit)}
                      className="space-y-3"
                      aria-labelledby="form-title"
                      aria-describedby="form-desc"
                    >
                      <div className="">
                        <section
                          className="flex flex-col "
                          aria-labelledby="payment_amount_label"
                        >
                          <Label
                            id="payment_amount_label"
                            htmlFor="payment_amount"
                          >
                            {translate('payment_amount')}{' '}
                          </Label>
                          <div className="relative mt-1 rounded-md ">
                            <Controller
                              name="payment_amount"
                              control={control}
                              rules={{ required: true, min: 1 }}
                              render={({ field }) => (
                                <CurrencyInputField
                                  id="payment_amount"
                                  error={errors.payment_amount?.message}
                                  value={field.value || ''}
                                  onChange={(value) => {
                                    field.onChange(value)
                                  }}
                                />
                              )}
                            />
                            {isAboveMaxAmount && (
                              <FormErrorMessage
                                message={t(
                                  'you_dont_have_enough_funds_to_cover'
                                )}
                              />
                            )}
                            <div className="text-sm text-gray-600 mt-2">
                              {t('total_available')}:{' '}
                              {currencyFormatter.format(maxAmount)}
                            </div>
                          </div>
                        </section>
                        <div className="mt-6">
                          <PaymentFeesResultsCard
                            isEnabled={isValidCalculation}
                            paymentFeesQuery={calculateFeesQuery}
                          />
                        </div>
                      </div>
                    </form>
                  </CardContent>
                </Card>
                <div className="flex gap-2 mt-10">
                  <Button
                    variant="outline"
                    onClick={onChange}
                    className="px-10"
                  >
                    {translate('back')}
                  </Button>{' '}
                  <SubmitButton
                    forceDisabled={
                      isAboveMaxAmount || calculateFeesQuery.isFetching
                    }
                    form="add-details-form"
                    status={'idle'}
                    buttonTxt={translate('next')}
                    className="px-10"
                  />
                </div>
              </div>
            )
          }}
        </MultiQueryPageLoader>
      </FamilyWizardLayout.Content>
    </FamilyWizardLayout>
  )
}
